import { isVariantOverrideAllowed, safeGet, safeSet } from "../utils/ABTestingCore";

/**
 * URL-based override for the server-side `scriptedChat` experiment
 * (see apps/functions/src/config/scripted-chat-experiment.ts and
 * apps/competition/mechat/scripted-chat-spec.md). Mirrors the
 * `?ab_<testName>=<variant>` convention every other test uses (e.g.
 * `?ab_kinkQuizVersion=v2|v3` in abTests.ts / ABTestingCore.ts), but this
 * isn't a registered ABTestConfig: cohort assignment is a deterministic hash
 * of the user id computed on the server, so there is no client-side weighted
 * default to select — only an optional, sticky override forwarded to the
 * `createChat` callable.
 *
 * Honored only on dev/staging web builds (isVariantOverrideAllowed — DEV,
 * VITE_DEPLOY_TARGET === "staging", or VITE_ENABLE_AB_OVERRIDE). The
 * functions side independently re-checks its own deploy target before
 * honoring the value it receives (see resolveScriptedChatVariant), so a
 * stale value can never affect production even if this guard were bypassed.
 *
 * Sticky in localStorage (`scriptedChatVariantOverride`) until the URL
 * carries a different value or the key is cleared manually.
 */

export type ScriptedChatVariantOverride = "scripted" | "control";

const QUERY_PARAM = "ab_scriptedChat";
const STORAGE_KEY = "scriptedChatVariantOverride";

function isValidVariant(value: string): value is ScriptedChatVariantOverride {
  return value === "scripted" || value === "control";
}

/**
 * Resolves the sticky override, if any. Returns undefined whenever overrides
 * aren't allowed (prod build), nothing has ever been set, or the stored/URL
 * value isn't a recognized variant — callers can pass the result straight
 * through to `createChat` without further validation.
 */
export function getScriptedChatVariantOverride(): ScriptedChatVariantOverride | undefined {
  if (!isVariantOverrideAllowed()) return undefined;
  if (typeof window === "undefined") return undefined;

  try {
    const fromUrl = new URLSearchParams(window.location.search).get(QUERY_PARAM);
    if (fromUrl) {
      if (isValidVariant(fromUrl)) {
        safeSet(STORAGE_KEY, fromUrl);
        return fromUrl;
      }
      console.warn(
        `[ScriptedChat] Ignoring "?${QUERY_PARAM}=${fromUrl}" — valid variants: scripted, control`
      );
    }
  } catch (error) {
    console.warn("Scripted chat variant override read failed:", error);
  }

  const stored = safeGet(STORAGE_KEY);
  return stored && isValidVariant(stored) ? stored : undefined;
}
